// Political view module extras (claims, city-state rings, fronts, units): see src/view/modules.ts.
// The base border layer lives in borders.ts; install() re-registers it with the claim texture, the
// front ribbons and the leader-lens switch. Owned by view-political.

import { uniform } from 'three/tsl';
import { HEX_COUNT } from '../../core/grid.ts';
import type { WorldSnapshot } from '../../world/api.ts';
import type { ViewMode } from '../api.ts';
import type { ViewCore } from '../index.ts';
import type { ViewModule } from '../modules.ts';
import { borderLayer } from './borders.ts';
import { Atlas } from './atlas.ts';
import { ringCells, ringLayer, ringPlan, unsourcedRings, type CityStateRing } from './cityStates.ts';
import { ClaimField } from './claims.ts';
import { FrontField, SCRUB_MS, frontRecords, qualityAfter, type FieldQuality, type FrontRecord } from './fronts.ts';
import { planAll, type UnitStack } from './stacks.ts';
import { UnitLayer } from './units.ts';

/** Lens id whose relation colours the borders take (world/lenses.ts). */
const LEADER_LENS = 'political';

/** What the UI reads besides the ViewModule hooks. */
export interface PoliticalModule extends ViewModule {
  /** City-state rings on the map, each with its sources. */
  rings(): readonly CityStateRing[];
  /** Fronts the map draws, each with its sources. */
  fronts(): readonly FrontRecord[];
  /** Army stacks drawn on the fronts now, each with its front's sources. */
  stacks(): readonly UnitStack[];
  /** The stack standing on a hex, for tooltips; null when none. */
  stackAt(hex: number): UnitStack | null;
}

function createPolitical(): PoliticalModule {
  const relationLens = uniform(0);
  let core: ViewCore | null = null;
  let atlas: Atlas | null = null;
  let claims: ClaimField | null = null;
  let field: FrontField | null = null;
  let units: UnitLayer | null = null;
  let mode: ViewMode = '3d';
  let records: FrontRecord[] = [];
  let due: WorldSnapshot | null = null;
  let ribbonsDue = false;
  let ribbonQuality: FieldQuality = 'full';
  let lastSnapshotAt = -Infinity;
  let plan: UnitStack[] = [];
  let rings: CityStateRing[] = [];
  let ringHexes = new Uint16Array(HEX_COUNT);
  let detach: Array<() => void> = [];

  /** Actor index of an actor id: labels carry it (ActorLabel.id); 0 when unknown. */
  const actorOf = (id: string): number => core?.labels.indexOf(id) ?? 0;

  function frame(): void {
    if (!core || !units) {
      return;
    }

    // Worked out here, once, whatever the number of snapshots since the last frame.
    if (due) {
      const snapshot = due;
      due = null;
      claims?.set(snapshot.claim);
      applyFronts(snapshot);
    }
    drawRibbons();

    // The country labels take in the snapshot and measure the names here while the globe hides them
    // (view/index.ts updates them only on the flat map), so their first flat frame only projects.
    core.labels.prepare();

    const pose = core.camera.pose();
    const map = core.engine.container;
    units.update(core.camera.camera, pose.x, pose.height, { width: map.clientWidth, height: map.clientHeight });
  }

  /**
   * One raster per frame at most, whatever the number of snapshots since: while the date is being
   * dragged the field is a sketch, and it is drawn in full once the snapshots stop.
   */
  function drawRibbons(): void {
    if (ribbonsDue) {
      ribbonsDue = false;
      field?.set(records, ribbonQuality);
      return;
    }

    if (performance.now() - lastSnapshotAt >= SCRUB_MS) {
      field?.refine();
    }
  }

  /** City-state rings follow the actor list, not the snapshot. */
  function applyRings(): void {
    if (!core) {
      return;
    }

    rings = ringPlan(core.labels.actorList());
    claims?.setRings(ringCells(rings, ringHexes));
  }

  function applyFronts(snapshot: WorldSnapshot): void {
    records = frontRecords(snapshot, actorOf);
    ribbonsDue = true;
    plan = planAll(records, snapshot.owner, snapshot.controller);
    units?.setStacks(plan);
  }

  return {
    name: 'political',
    async install(c) {
      core = c;
      atlas = new Atlas();
      claims = new ClaimField(atlas);
      const ribbons = new FrontField(atlas);
      field = ribbons;
      c.surface.register(borderLayer({ claims, relationLens, frontCover: (ctx) => ribbons.cover(ctx) }));
      c.surface.register(ribbons.layer());
      c.surface.register(ringLayer(claims));
      detach.push(c.labels.onActors(applyRings));
      applyRings();

      units = await UnitLayer.create(c);
      units.setMode(mode);
      units.setStacks(plan);
      c.engine.scene.add(units.group);
      detach.push(c.engine.onFrame(frame));
    },
    warm(x, y, z) {
      units?.warm(x, y, z);
    },
    setSnapshot(snapshot) {
      const now = performance.now();
      ribbonQuality = qualityAfter(now - lastSnapshotAt);
      lastSnapshotAt = now;
      due = snapshot;
    },
    setLens(lens) {
      relationLens.value = lens?.id === LEADER_LENS ? 1 : 0;
    },
    setMode(next) {
      mode = next;
      units?.setMode(next);
    },
    unsourced: () => unsourcedRings(rings) + (units?.drawn().filter((s) => s.sources.length === 0).length ?? 0),
    rings: () => rings,
    fronts: () => records,
    stacks: () => units?.drawn() ?? [],
    stackAt: (hex) => units?.stackAt(hex) ?? null,
    dispose() {
      for (const off of detach) {
        off();
      }
      detach = [];
      if (units) {
        core?.engine.scene.remove(units.group);
        units.dispose();
      }
      atlas?.dispose();
      units = null;
      atlas = null;
      claims = null;
      field = null;
      core = null;
    },
  };
}

export const political: PoliticalModule = createPolitical();
